import {
  ExportKeyRowKind,
  IBox,
  IExportKeyEntry,
  IExportKeyRow,
  IExportKeyTable,
  LabelMeasure,
  MAX_KEY_ENTRIES,
} from './exportLabelTypes'
import { IExportSheet } from './mapExportPaper'

export const KEY_TABLE_INSET_PX = 12

export const KEY_TABLE_TEXT_PX = 10
const KEY_TABLE_WEIGHT = 400
const KEY_TABLE_TITLE_WEIGHT = 600
// Tall enough for the boxed tag, which reads as the map's own.
const KEY_TABLE_LINE_HEIGHT = 1.6
const KEY_TAG_BOX_PX = 14
const KEY_TAG_BOX_PADDING_PX = 3
// Half a row before each group, so groups read as blocks.
const KEY_GROUP_GAP_ROWS = 0.5
const KEY_TABLE_PADDING_PX = 6
const KEY_TABLE_COLUMN_GAP_PX = 12
const KEY_TAG_GAP_PX = 6
// The small counter disk drawn before a group's header, so the row reads as the map's disk.
const KEY_GROUP_GLYPH_PX = 8
const KEY_GROUP_GLYPH_GAP_PX = 4
// A row is cut with an ellipsis past this, at ×1, so one long title cannot widen the whole strip.
export const KEY_ROW_MAX_PX = 260
const ELLIPSIS = '…'

// The key's room at any scale: the key scales its own sizes and works out the rows and columns.
export interface IKeyTableLayout {
  heightPx: number
  widthPx: number
  // A PNG's strip stops widening here; a PDF's page 2 takes what its width holds.
  maxColumns?: number
  // Room kept clear above the key, e.g. for the watermark on a PDF's page 2.
  topPx?: number
}

export interface IKeyTableCopy {
  title: string
  groupHeader: (count: number) => string
  groupHeaderTrimmed: (count: number, listed: number) => string
  overflowRow: (count: number) => string
}

export interface IKeyTableRow {
  kind: ExportKeyRowKind
  tag: string
  text: string
  x: number
  y: number
  // From the row's start to its text: the tag column, and a group's disk after it.
  indentPx: number
  weight: number
  glyph: { x: number; y: number; radius: number } | null
  // The tag drawn as the map's square tag, so a tag on the map is found in the key at a glance.
  tagBox: IBox | null
}

export function fitKeyRow(
  text: string,
  sizePx: number,
  weight: number,
  measure: LabelMeasure,
  maxPx = KEY_ROW_MAX_PX,
): string {
  if (measure(text, sizePx, weight, 0) <= maxPx) {
    return text
  }
  // The longest start that fits with the ellipsis, else one character; widths grow with length.
  let end = 1
  let high = text.length
  while (end < high) {
    const mid = Math.ceil((end + high) / 2)
    if (measure(text.slice(0, mid) + ELLIPSIS, sizePx, weight, 0) <= maxPx) {
      end = mid
    } else {
      high = mid - 1
    }
  }
  return text.slice(0, end).trimEnd() + ELLIPSIS
}

const rowWeight = (kind: ExportKeyRowKind): number =>
  kind === 'title' || kind === 'group'
    ? KEY_TABLE_TITLE_WEIGHT
    : KEY_TABLE_WEIGHT

// A group's names line up under its header's text, so each group reads as one block.
const rowIndent = (
  kind: ExportKeyRowKind,
  tagColumnPx: number,
  scale: number,
): number => {
  switch (kind) {
    case 'single':
      return tagColumnPx
    case 'group':
    case 'member':
      return tagColumnPx + (KEY_GROUP_GLYPH_PX + KEY_GROUP_GLYPH_GAP_PX) * scale
    default:
      return 0
  }
}

export const isKeyGroup = (
  entry: IExportKeyEntry,
): entry is Extract<IExportKeyEntry, { members: string[] }> =>
  'members' in entry

// The largest per-group share k such that listing min(size, k) of every group fits the budget:
// every group gives up its oldest names evenly, and none is cut while another still lists more.
function memberShare(sizes: number[], budget: number): number {
  const listed = (k: number) =>
    sizes.reduce((sum, size) => sum + Math.min(size, k), 0)
  const largest = Math.max(0, ...sizes)
  let k = 0
  while (k < largest && listed(k + 1) <= budget) {
    k += 1
  }
  return k
}

const keyRowHeightPx = (scale: number): number =>
  KEY_TABLE_TEXT_PX * KEY_TABLE_LINE_HEIGHT * scale

// Rows per column, and as many columns of the widest row as the width holds: worst-case rows, so
// the key never runs past its page or strip, whatever it lists.
function keyTableGrid(
  layout: IKeyTableLayout,
  scale: number,
): { rows: number; columns: number } {
  const padding = 2 * KEY_TABLE_PADDING_PX * scale
  const height =
    layout.heightPx - (layout.topPx ?? 0) - 2 * KEY_TABLE_INSET_PX - padding
  const width =
    layout.widthPx -
    2 * KEY_TABLE_INSET_PX -
    padding +
    KEY_TABLE_COLUMN_GAP_PX * scale
  return {
    rows: Math.max(1, Math.floor(height / keyRowHeightPx(scale))),
    columns: Math.min(
      layout.maxColumns ?? Infinity,
      Math.max(
        1,
        Math.floor(
          width / ((KEY_ROW_MAX_PX + KEY_TABLE_COLUMN_GAP_PX) * scale),
        ),
      ),
    ),
  }
}

// The rows a key promises to hold at `scale`: one per column is kept for a header pushed on to
// stay with its first name.
export function keyTableCapacity(
  layout: IKeyTableLayout,
  scale: number,
): number {
  const { rows, columns } = keyTableGrid(layout, scale)
  return rows * columns - columns
}

// What is left for numbered labels once the title, the last row and `groups` lettered groups
// (each a header and half a row before it) are counted; negative when the groups do not fit.
export const keyRoomForSingles = (capacity: number, groups: number): number =>
  capacity - 2 - groups - Math.ceil(groups * KEY_GROUP_GAP_ROWS)

// The largest scale at which the key still holds its title, MAX_KEY_ENTRIES numbered labels and
// its last row, from worst-case rows and columns, so nothing the key lists feeds back into its
// size. Never under ×1: a smaller room lists fewer instead.
export function keyTableFit(layout: IKeyTableLayout): number {
  const rowsNeeded = MAX_KEY_ENTRIES + 2
  const height = layout.heightPx - (layout.topPx ?? 0) - 2 * KEY_TABLE_INSET_PX
  const width = layout.widthPx - 2 * KEY_TABLE_INSET_PX
  const maxColumns = Math.min(layout.maxColumns ?? rowsNeeded, rowsNeeded)
  let fit = 0
  for (let columns = 1; columns <= maxColumns; columns += 1) {
    const rows = Math.ceil(rowsNeeded / columns) + 1
    const byRows =
      height /
      (rows * KEY_TABLE_TEXT_PX * KEY_TABLE_LINE_HEIGHT +
        2 * KEY_TABLE_PADDING_PX)
    const byColumns =
      width /
      (columns * (KEY_ROW_MAX_PX + KEY_TABLE_COLUMN_GAP_PX) -
        KEY_TABLE_COLUMN_GAP_PX +
        2 * KEY_TABLE_PADDING_PX)
    fit = Math.max(fit, Math.min(byRows, byColumns))
  }
  return Math.max(1, fit)
}

// Rows fill each column down; a group opens with a gap and never leaves its header alone at a
// column's foot. Counted in rows, so a scaled row height never adds up past a column's foot.
function fillKeyColumns(
  rows: IExportKeyRow[],
  rowsPerColumn: number,
): { rowColumns: number[]; rowTops: number[]; columns: number } {
  const rowColumns: number[] = []
  const rowTops: number[] = []
  let column = 0
  let top = 0
  rows.forEach((row, index) => {
    const isGroup = row.kind === 'group'
    const gap = isGroup && top > 0 ? KEY_GROUP_GAP_ROWS : 0
    // A numbered stack keeps its name with the first thing it holds, as a group its header.
    const keptRows =
      (isGroup || row.kind === 'single') && rows[index + 1]?.kind === 'member'
        ? 2
        : 1
    if (top > 0 && top + gap + keptRows > rowsPerColumn) {
      column += 1
      top = 0
    } else {
      top += gap
    }
    rowColumns.push(column)
    rowTops.push(top)
    top += 1
  })
  return { rowColumns, rowTops, columns: column + 1 }
}

// The key lives on its own, never over the map: a title, the numbered labels, then each lettered
// group with its names, filled down each column, every size at `scale`. Past the room `layout`
// gives, headers and numbered labels always stay; groups list fewer names, oldest dropped first,
// and one last row counts all that is not listed, with the `overCap` names that never got a tag.
export function layoutKeyTable(
  entries: IExportKeyEntry[],
  overCap: number,
  layout: IKeyTableLayout,
  copy: IKeyTableCopy,
  measure: LabelMeasure,
  scale: number,
): { table: IExportKeyTable | null; trimmed: number } {
  if (!entries.length && !overCap) {
    return { table: null, trimmed: 0 }
  }
  const sizePx = KEY_TABLE_TEXT_PX * scale
  const rowHeightPx = keyRowHeightPx(scale)
  const paddingPx = KEY_TABLE_PADDING_PX * scale
  const topPx = layout.topPx ?? 0
  const grid = keyTableGrid(layout, scale)

  const groups = entries.filter(isKeyGroup)
  // Group gaps, and a row per column a header may push on to keep with its first name.
  const capacity =
    keyTableCapacity(layout, scale) -
    Math.ceil(groups.length * KEY_GROUP_GAP_ROWS)
  const sizes = groups.map(group => group.members.length)
  const memberTotal = sizes.reduce((sum, size) => sum + size, 0)
  const detailRows = entries.reduce(
    (sum, entry) =>
      sum + (isKeyGroup(entry) ? 0 : (entry.details?.length ?? 0)),
    0,
  )
  const fixedRows = 1 + entries.length + detailRows
  const fitsAll = fixedRows + memberTotal + (overCap > 0 ? 1 : 0) <= capacity
  const trimmedAt = (share: number): number =>
    memberTotal - sizes.reduce((sum, size) => sum + Math.min(size, share), 0)
  const rowsAt = (share: number): IExportKeyRow[] => {
    const overflow = overCap + trimmedAt(share)
    return [
      { kind: 'title', tag: '', text: copy.title },
      ...entries.flatMap((entry): IExportKeyRow[] => {
        if (!isKeyGroup(entry)) {
          return [
            { kind: 'single', tag: entry.tag, text: entry.text },
            ...(entry.details ?? []).map(text => ({
              kind: 'member' as const,
              tag: '',
              text,
            })),
          ]
        }
        const listed = entry.members.slice(0, share)
        return [
          {
            kind: 'group',
            tag: entry.tag,
            text:
              listed.length < entry.members.length
                ? copy.groupHeaderTrimmed(entry.count, listed.length)
                : copy.groupHeader(entry.count),
          },
          ...listed.map(text => ({ kind: 'member' as const, tag: '', text })),
        ]
      }),
      ...(overflow > 0
        ? [
            {
              kind: 'overflow' as const,
              tag: '',
              text: copy.overflowRow(overflow),
            },
          ]
        : []),
    ]
  }
  // A header kept with its first name can leave more of a column empty than the capacity keeps
  // for it; each group then lists one name fewer until the key is back inside its columns.
  let share = fitsAll ? Infinity : memberShare(sizes, capacity - fixedRows - 1)
  let rows = rowsAt(share)
  let placed = fillKeyColumns(rows, grid.rows)
  while (placed.columns > grid.columns && share > 0) {
    share = Math.min(share, Math.max(...sizes)) - 1
    rows = rowsAt(share)
    placed = fillKeyColumns(rows, grid.rows)
  }
  const trimmed = trimmedAt(share)

  const tagBoxPx = Math.max(
    KEY_TAG_BOX_PX * scale,
    ...entries.map(
      entry =>
        measure(entry.tag, sizePx, KEY_TABLE_TITLE_WEIGHT, 0) +
        2 * KEY_TAG_BOX_PADDING_PX * scale,
    ),
  )
  const tagColumnPx = tagBoxPx + KEY_TAG_GAP_PX * scale
  const fitted = rows.map(row => {
    const indentPx = rowIndent(row.kind, tagColumnPx, scale)
    return {
      ...row,
      text: fitKeyRow(
        row.text,
        sizePx,
        rowWeight(row.kind),
        measure,
        KEY_ROW_MAX_PX * scale - indentPx,
      ),
    }
  })
  const widest = Math.max(
    ...fitted.map(
      row =>
        rowIndent(row.kind, tagColumnPx, scale) +
        measure(row.text, sizePx, rowWeight(row.kind), 0),
    ),
  )
  const { rowColumns, columns } = placed
  const rowTops = placed.rowTops.map(top => top * rowHeightPx)
  const columnGapPx = KEY_TABLE_COLUMN_GAP_PX * scale
  const columnWidthPx = widest + columnGapPx
  const width = columns * columnWidthPx - columnGapPx + 2 * paddingPx
  const height =
    Math.max(...rowTops.map(rowTop => rowTop + rowHeightPx)) + 2 * paddingPx
  return {
    table: {
      box: {
        x: KEY_TABLE_INSET_PX,
        y: topPx + KEY_TABLE_INSET_PX,
        width,
        height,
      },
      rows: fitted,
      columns,
      rowColumns,
      rowTops,
      columnWidthPx,
      rowHeightPx,
      paddingPx,
      sizePx,
      tagColumnPx,
      tagBoxPx,
      scale,
    },
    trimmed,
  }
}

export const keyStripWidth = (table: IExportKeyTable): number =>
  table.box.width + 2 * KEY_TABLE_INSET_PX

// The file a sheet makes, in px: the map, the band under it, and a key strip beside the map when
// it has one (a PNG's).
export const sheetFilePx = (
  sheet: IExportSheet,
  keyBeside: IExportKeyTable | null,
): { pxWidth: number; pxHeight: number } => ({
  pxWidth:
    sheet.map.pxWidth +
    (keyBeside
      ? Math.ceil(keyStripWidth(keyBeside) * sheet.map.pixelRatio)
      : 0),
  pxHeight: sheet.map.pxHeight + sheet.bandPxHeight,
})

// Row tops in strip px; both renderers place each row's baseline from its top the same way.
export function keyTableRows(table: IExportKeyTable): IKeyTableRow[] {
  return table.rows.map((row, index) => {
    const x =
      table.box.x +
      table.paddingPx +
      table.rowColumns[index] * table.columnWidthPx
    const y = table.box.y + table.paddingPx + table.rowTops[index]
    const radius = (KEY_GROUP_GLYPH_PX / 2) * table.scale
    const tagBoxHeightPx = KEY_TAG_BOX_PX * table.scale
    return {
      ...row,
      x,
      y,
      indentPx: rowIndent(row.kind, table.tagColumnPx, table.scale),
      weight: rowWeight(row.kind),
      glyph:
        row.kind === 'group'
          ? {
              x: x + table.tagColumnPx + radius,
              y: y + table.rowHeightPx / 2,
              radius,
            }
          : null,
      tagBox: row.tag
        ? {
            x,
            y: y + (table.rowHeightPx - tagBoxHeightPx) / 2,
            width: table.tagBoxPx,
            height: tagBoxHeightPx,
          }
        : null,
    }
  })
}
